import { useState, useEffect } from "react";
import { NodeStates } from "../build/types";

export type InteractionStates = {
    [key in NodeStates]?: boolean;
};

const defaultState = {
    [NodeStates.HOVER]: false,
    [NodeStates.ACTIVE]: false,
    [NodeStates.FOCUS]: false
};

export const useInteractionStates = <T extends HTMLElement = HTMLElement>() => {
    const [element, setElement] = useState<T | null>(null);
    const [states, setStates] = useState<InteractionStates>(defaultState);

    const handleMouseEnter = () => setStates(s => ({ ...s, [NodeStates.HOVER]: true }));
    const handleMouseLeave = () =>
        setStates(s => ({
            ...s,
            [NodeStates.HOVER]: false,
            [NodeStates.ACTIVE]: false
        }));
    const handleMouseDown = () => setStates(s => ({ ...s, [NodeStates.ACTIVE]: true }));
    const handleMouseUp = () => setStates(s => ({ ...s, [NodeStates.ACTIVE]: false }));
    const handleFocus = () => setStates(s => ({ ...s, [NodeStates.FOCUS]: true }));
    const handleBlur = () => setStates(s => ({ ...s, [NodeStates.FOCUS]: false }));
    const handleTouchStart = () => setStates(s => ({ ...s, [NodeStates.ACTIVE]: true }));
    const handleTouchEnd = () =>
        setStates(s => ({
            ...s,
            [NodeStates.ACTIVE]: false,
            [NodeStates.HOVER]: false
        }));

    const resetStates = () => {
        setStates(defaultState);
    };

    const addListeners = (element: T) => {
        element.addEventListener("mouseenter", handleMouseEnter);
        element.addEventListener("mouseleave", handleMouseLeave);
        element.addEventListener("mousedown", handleMouseDown);
        element.addEventListener("mouseup", handleMouseUp);
        element.addEventListener("focus", handleFocus);
        element.addEventListener("blur", handleBlur);
        element.addEventListener("touchstart", handleTouchStart);
        element.addEventListener("touchend", handleTouchEnd);
    };

    const removeListeners = (element: T) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
        element.removeEventListener("mousedown", handleMouseDown);
        element.removeEventListener("mouseup", handleMouseUp);
        element.removeEventListener("focus", handleFocus);
        element.removeEventListener("blur", handleBlur);
        element.addEventListener("touchstart", handleTouchStart);
        element.addEventListener("touchend", handleTouchEnd);
    };

    useEffect(() => {
        if (element) {
            addListeners(element);
        } else {
            resetStates();
        }

        // Cleanup listeners on ref change or component unmount
        return () => {
            if (element) {
                removeListeners(element);
            }
        };
    }, [element]); // Ensure this effect runs when interactionRef.current changes

    return { states, element, setElement, resetStates };
};
